import CheckboxTree from "react-checkbox-tree";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState, useMemo } from "react";
import { library } from "@fortawesome/fontawesome-svg-core";
import {
  faCheckSquare,
  faSquare,
  faChevronRight,
  faChevronDown,
  faPlusSquare,
  faMinusSquare,
  faFolder,
  faFolderOpen,
  faFile,
} from "@fortawesome/free-solid-svg-icons";

library.add(
  faCheckSquare,
  faSquare,
  faChevronRight,
  faChevronDown,
  faPlusSquare,
  faMinusSquare,
  faFolder,
  faFolderOpen,
  faFile
);

type RNCheckBoxTreeProps = {
  data: any[];
  checked_nodes: any[];
  on_node_selection: (nodes: any[]) => void;
};

const filterNodes = (nodes, query) => {
  const queryLower = String(query || "").toLowerCase().trim();
  const tokens = queryLower.split(/[\s,]+/).filter(Boolean); // supports: "101,102,104"

  // If no search, return full tree
  if (tokens.length === 0) return nodes;

  return (nodes || []).reduce((acc, node) => {
    const label = String(node?.label || "").toLowerCase();
    const labelMatch = tokens.some((t) => label.includes(t));

    if (labelMatch) {
      acc.push({
        ...node,
        children: node.children ? node.children : [],
      });
    } else if (node.children) {
      const filteredChildren = filterNodes(node.children, query);
      if (filteredChildren.length > 0) {
        acc.push({
          ...node,
          children: filteredChildren,
        });
      }
    }

    return acc;
  }, []);
};

const collectLeafValues = (nodes) => {
  const out = [];

  const walk = (list) => {
    for (const node of list || []) {
      const hasChildren = Array.isArray(node.children) && node.children.length > 0;
      if (hasChildren) walk(node.children);
      else if (node.value != null) out.push(node.value);
    }
  };

  walk(nodes);
  return out;
};

export const RNCheckBoxTree = (props: RNCheckBoxTreeProps) => {
  const { checked_nodes, data, on_node_selection } = props;
  const [expanded, setExpanded] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");

  // Memoized filtered data
  const filteredData = useMemo(() => {
    return filterNodes(data, searchQuery);
  }, [data, searchQuery]);

  // Leaf values currently visible in the filtered tree (used to preserve hidden selections)
  const visibleLeafValues = useMemo(() => collectLeafValues(filteredData), [filteredData]);

  return (
    <div className={"flex flex-col gap-2 "}>
      <input
        type="text"
        placeholder="Search..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="border-2 p-2 w-full rounded-[8px] focus:outline-none border-slate-200"
      />
      <CheckboxTree
        nodes={filteredData}
        checked={checked_nodes}
        expanded={expanded}
        checkModel={"leaf"}
        onCheck={(check) => {
          // react-checkbox-tree emits ONLY what's visible in `nodes`.
          // When searching, preserve selections that are currently hidden by the filter.
          const visibleSet = new Set(visibleLeafValues);
          const preserved = (checked_nodes || []).filter((v) => !visibleSet.has(v));
          const merged = Array.from(new Set([...(preserved || []), ...(check || [])]));
          on_node_selection(merged);
        }}
        onExpand={(expand) => {
          setExpanded(expand);
        }}
        showExpandAll
        icons={{
          check: <FontAwesomeIcon icon="check-square" />,
          uncheck: <FontAwesomeIcon icon="square" />,
          halfCheck: (
            <FontAwesomeIcon
              icon="check-square"
              className="rct-icon rct-icon-half-check"
            />
          ),
          expandClose: (
            <FontAwesomeIcon
              icon="chevron-right"
              className="rct-icon rct-icon-expand-close"
            />
          ),
          expandOpen: (
            <FontAwesomeIcon
              icon="chevron-down"
              className="rct-icon rct-icon-expand-open"
            />
          ),
          expandAll: (
            <FontAwesomeIcon
              icon="plus-square"
              className="rct-icon rct-icon-expand-all"
            />
          ),
          collapseAll: (
            <FontAwesomeIcon
              icon="minus-square"
              className="rct-icon rct-icon-collapse-all"
            />
          ),
          parentClose: (
            <FontAwesomeIcon
              icon="folder"
              className="rct-icon rct-icon-parent-close"
            />
          ),
          parentOpen: (
            <FontAwesomeIcon
              icon="folder-open"
              className="rct-icon rct-icon-parent-open"
            />
          ),
          leaf: (
            <FontAwesomeIcon
              icon="file"
              className="rct-icon rct-icon-leaf-close"
            />
          ),
        }}
      />
    </div>
  );
};
